import React, { useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Stack,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import FavoriteIcon from "@mui/icons-material/Favorite";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import BusinessIcon from "@mui/icons-material/Business";

const UpgradeToPro = () => {
  const [currentPlan] = useState("HOBBY"); // Default plan

  const plans = [
    {
      id: "hobby",
      name: "Hobby",
      price: "$0",
      period: "/month",
      description: "Free forever",
      icon: FavoriteIcon,
      features: [
        { text: "1k connected accounts", included: true },
        { text: "20K tool calls/mo", included: true },
        { text: "14 days log retention", included: true },
        { text: "Priority support", included: false },
      ],
      buttonText: "Current Plan",
      buttonVariant: "outlined",
      isCurrent: true,
    },
    {
      id: "starter",
      name: "Starter",
      price: "$29",
      period: "/month",
      description: "For solo projects",
      icon: RocketLaunchIcon,
      features: [
        { text: "30K connected accounts", included: true },
        { text: "200K tool calls/mo", included: true },
        { text: "30 days log retention", included: true },
        { text: "Priority support", included: false },
      ],
      buttonText: "Upgrade to Starter",
      buttonVariant: "contained",
      isCurrent: false,
    },
    {
      id: "growth",
      name: "Growth",
      price: "$229",
      period: "/month",
      description: "For growing teams",
      icon: TrendingUpIcon,
      features: [
        { text: "100K connected accounts", included: true },
        { text: "2M tool calls/mo", included: true },
        { text: "90 days log retention", included: true },
        { text: "Priority support", included: true },
      ],
      buttonText: "Upgrade to Growth",
      buttonVariant: "contained",
      isCurrent: false,
    },
    {
      id: "enterprise",
      name: "Enterprise",
      price: "Custom",
      period: "",
      description: "For enterprise teams",
      icon: BusinessIcon,
      features: [
        { text: "Flexible connected accounts", included: true },
        { text: "Flexible tool calls", included: true },
        { text: "Flexible log retention", included: true },
        { text: "24/7 Enterprise support", included: true },
      ],
      buttonText: "Contact Sales",
      buttonVariant: "contained",
      isCurrent: false,
    },
  ];

  const handleUpgrade = (planId) => {
    // Handle upgrade logic here
    console.log(`Upgrading to ${planId} plan`);
  };

  const handleContactSales = () => {
    // Handle contact sales logic here
    console.log("Contact sales clicked");
  };

  return (
    <Box
      sx={{
        bgcolor: "#1E1E1E",
        color: "#fff",
        height: "calc(100vh - 100px)",
        overflowY: "auto",
        "&::-webkit-scrollbar": {
          display: "none",
        },
        "-ms-overflow-style": "none",
        "scrollbar-width": "none",
      }}
    >
      <Box
        sx={{
          py: 3,
          px: { xs: 2, md: 3 },
          maxWidth: "100%",
          width: "100%",
          boxSizing: "border-box",
          margin: 0,
        }}
      >
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Typography
            variant="body1"
            sx={{
              color: "#AEAEAE",
              mb: 4,
              fontSize: "1.1rem",
            }}
          >
            Choose the perfect plan for your needs. All plans include core
            features with flexible scaling options.
          </Typography>
        </Box>

        {/* Current Plan Section */}
        <Box sx={{ mb: 6 }}>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 600,
              mb: 3,
              color: "#EDEDED",
            }}
          >
            Current Plan
          </Typography>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 3,
              p: 3,
              borderRadius: "12px",
              background: "#2C2F33",
              border: "2px solid rgba(185, 227, 7, 0.4)",
              position: "relative",
              "&::before": {
                content: '""',
                position: "absolute",
                top: -2,
                left: -2,
                right: -2,
                bottom: -2,
                borderRadius: "14px",
                background:
                  "linear-gradient(135deg, rgba(185, 227, 7, 0.3) 0%, rgba(207, 255, 4, 0.2) 50%, rgba(185, 227, 7, 0.3) 100%)",
                zIndex: -1,
              },
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: 60,
                height: 60,
                borderRadius: "16px",
                flexShrink: 0,
                position: "relative",
                background:
                  "linear-gradient(135deg, rgba(185, 227, 7, 0.5) 0%, rgba(207, 255, 4, 0.7) 100%)",
                backdropFilter: "blur(12px)",
                border: "1px solid rgba(185, 227, 7, 0.7)",
                boxShadow:
                  "0 12px 40px rgba(185, 227, 7, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.3)",
                "&::before": {
                  content: '""',
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  borderRadius: "16px",
                  background:
                    "linear-gradient(135deg, rgba(255, 255, 255, 0.15) 0%, rgba(255, 255, 255, 0) 100%)",
                  pointerEvents: "none",
                },
              }}
            >
              <FavoriteIcon
                sx={{
                  fontSize: 32,
                  color: "#B9E307",
                  filter: "drop-shadow(0 2px 6px rgba(0, 0, 0, 0.3))",
                  zIndex: 1,
                }}
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography
                sx={{
                  color: "#EDEDED",
                  fontSize: "1.1rem",
                  fontWeight: 500,
                  mb: 0.5,
                }}
              >
                Current Plan
              </Typography>
              <Typography
                sx={{
                  color: "#B9E307",
                  fontSize: "1.2rem",
                  fontWeight: 600,
                }}
              >
                You are currently on the{" "}
                <strong style={{ fontWeight: 700 }}>{currentPlan}</strong> plan
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Plans Grid */}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            flexWrap: "wrap",
            gap: { xs: 2, md: 2.5 },
            justifyContent: "flex-start",
            alignItems: "stretch",
            width: "100%",
            maxWidth: "100%",
            boxSizing: "border-box",
          }}
        >
          {plans.map((plan) => (
            <Card
              key={plan.id}
              sx={{
                bgcolor: "#2C2F3366",
                border: plan.isCurrent
                  ? "2px solid #B9E307"
                  : "1px solid #50505030",
                borderRadius: "4px",
                position: "relative",
                height: "fit-content",
                flex: {
                  xs: "1 1 100%", // Full width on mobile
                  sm: "1 1 calc(50% - 1.25rem)", // 2 columns on small+
                  md: "1 1 calc(50% - 1.25rem)", // 2 columns on medium
                  lg: "1 1 calc(33.333% - 1.67rem)", // 3 columns on large
                  xl: "1 1 calc(25% - 1.875rem)", // 4 columns on xl
                },
                minWidth: 0,
                maxWidth: "100%",
                boxSizing: "border-box",
                transition: "all 0.3s ease",
                "&:hover": {
                  borderColor: plan.isCurrent ? "#B9E307" : "#888",
                  transform: "translateY(-4px)",
                },
              }}
            >
              <CardContent
                sx={{
                  p: { xs: 1.5, sm: 2, md: 3 },
                  "&:last-child": { pb: { xs: 1.5, sm: 2, md: 3 } },
                }}
              >
                {/* Plan Header */}
                <Box sx={{ mb: 3 }}>
                  {/* Plan Icon */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: 70,
                      height: 70,
                      borderRadius: "16px",
                      mb: 3,
                      mx: "auto",
                      position: "relative",
                      background: plan.isCurrent
                        ? "linear-gradient(135deg, rgba(185, 227, 7, 0.4) 0%, rgba(207, 255, 4, 0.6) 100%)"
                        : "linear-gradient(135deg, rgba(207, 255, 4, 0.3) 0%, rgba(185, 227, 7, 0.5) 100%)",
                      backdropFilter: "blur(10px)",
                      border: "1px solid",
                      borderColor: plan.isCurrent
                        ? "rgba(185, 227, 7, 0.6)"
                        : "rgba(207, 255, 4, 0.4)",
                      boxShadow: plan.isCurrent
                        ? "0 8px 32px rgba(185, 227, 7, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.2)"
                        : "0 8px 32px rgba(207, 255, 4, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.1)",
                      "&::before": {
                        content: '""',
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        borderRadius: "16px",
                        background:
                          "linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0) 100%)",
                        pointerEvents: "none",
                      },
                    }}
                  >
                    <plan.icon
                      sx={{
                        fontSize: 36,
                        color: plan.isCurrent ? "#B9E307" : "#CFFF04",
                        filter: "drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2))",
                        zIndex: 1,
                      }}
                    />
                  </Box>

                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 600,
                      color: "#EDEDED",
                      mb: 1,
                      textAlign: "center",
                    }}
                  >
                    {plan.name}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "#AEAEAE",
                      mb: 3,
                      textAlign: "center",
                    }}
                  >
                    {plan.description}
                  </Typography>

                  {/* Price */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "baseline",
                      mb: 4,
                      justifyContent: "center",
                    }}
                  >
                    <Typography
                      variant="h3"
                      sx={{
                        fontWeight: 700,
                        color: "#EDEDED",
                        mr: 0.5,
                      }}
                    >
                      {plan.price}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "#AEAEAE",
                      }}
                    >
                      {plan.period}
                    </Typography>
                  </Box>
                </Box>

                {/* Features */}
                <Stack spacing={2} sx={{ mb: 4 }}>
                  {plan.features.map((feature, index) => (
                    <Box
                      key={index}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                      }}
                    >
                      <CheckIcon
                        sx={{
                          fontSize: 18,
                          color: feature.included ? "#B9E307" : "#666",
                        }}
                      />
                      <Typography
                        variant="body2"
                        sx={{
                          color: feature.included ? "#EDEDED" : "#666",
                          fontSize: "0.95rem",
                        }}
                      >
                        {feature.text}
                      </Typography>
                    </Box>
                  ))}
                </Stack>

                {/* Action Button */}
                <Button
                  variant={plan.buttonVariant}
                  fullWidth
                  disabled={plan.isCurrent}
                  onClick={() => {
                    if (plan.id === "enterprise") {
                      handleContactSales();
                    } else {
                      handleUpgrade(plan.id);
                    }
                  }}
                  sx={{
                    py: 1.5,
                    fontSize: "1rem",
                    fontWeight: 600,
                    borderRadius: "4px",
                    textTransform: "capitalize",
                    bgcolor:
                      plan.buttonVariant === "contained" && !plan.isCurrent
                        ? "#CFFF04"
                        : "transparent",
                    color: plan.isCurrent
                      ? "#B9E307"
                      : plan.buttonVariant === "contained"
                      ? "#000"
                      : "#EDEDED",
                    borderColor: plan.isCurrent ? "#B9E307" : "#CFFF04",
                    "&:hover": {
                      bgcolor:
                        plan.buttonVariant === "contained" && !plan.isCurrent
                          ? "#B9E307"
                          : "rgba(185, 227, 7, 0.1)",
                      borderColor: "#B9E307",
                    },
                    "&.Mui-disabled": {
                      color: "#B9E307",
                      borderColor: "#B9E307",
                      bgcolor: "transparent",
                    },
                  }}
                >
                  {plan.isCurrent && <CheckIcon sx={{ mr: 1, fontSize: 18 }} />}
                  {plan.buttonText}
                </Button>
              </CardContent>
            </Card>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default UpgradeToPro;
